// @ds/connected/EntityAuditMeta
//
// Canonical "Szczegóły" sidebar card — created_at / created_by / updated_at /
// updated_by — usable by every entity view.
//
// Reads from `entity_audit_log_view`: first INSERT row gives the creator,
// most-recent UPDATE row gives the latest editor. The created_at / updated_at
// timestamps come from the entity record itself (caller passes them in).
// One small connected component instead of N copy-pasted per-entity cards.

import React from "react";
import { useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import type { AuditEntityType } from "@shared/types/audit";
import { Card } from "@ds/layout/Card";
import { KVGrid, type KVItem } from "@ds/layout/KVGrid";
import { IconText } from "@ds/display/IconText";
import { DateValue } from "@ds/display/DateValue";
import { TextValue } from "@ds/display/TextValue";

// ─── Inputs ──────────────────────────────────────────────────────────────────

export interface EntityAuditMetaLabels {
  /** Card title override. Default: `t("common.sections.details")`. */
  title?: string;
  /** Created-at row label. Default: `t("common.audit.created_at")`. */
  createdAt?: string;
  /** Created-by row label. Default: `t("common.audit.created_by")`. */
  createdBy?: string;
  /** Updated-at row label. Default: `t("common.audit.updated_at")`. */
  updatedAt?: string;
  /** Updated-by row label. Default: `t("common.audit.updated_by")`. */
  updatedBy?: string;
}

export interface EntityAuditMetaInput {
  /** Audit-log entity_type literal (e.g. "invoice", "transaction"). */
  entityType: AuditEntityType;
  /** Numeric entity_id whose audit history is queried. */
  entityId: number;
  /** Entity's own created_at timestamp (ISO). */
  createdAt: string | null | undefined;
  /** Entity's own updated_at timestamp (ISO). */
  updatedAt: string | null | undefined;
  /**
   * Card title — defaults to "Szczegóły".
   * @deprecated Use `labels.title` instead.
   */
  title?: string;
  /**
   * Optional label overrides for the title + row labels. Entities that need
   * gendered copy ("Utworzona" for `faktura`, "Utworzony" for `dokument`)
   * pass overrides here instead of forking the primitive.
   */
  labels?: EntityAuditMetaLabels;
  /** Inline extra rows (entity-specific) rendered ABOVE the audit rows. */
  extraRows?: KVItem[];
  testId?: string;
}

export type EntityAuditMetaProps = EntityAuditMetaInput;

interface ActorRow {
  action: "INSERT" | "UPDATE" | "DELETE";
  actor_name: string | null;
  created_at: string;
}

const FIRST_LAST_SELECT = "action,actor_name,created_at";

export const EntityAuditMeta: React.FC<EntityAuditMetaProps> = ({
  entityType, entityId, createdAt, updatedAt, title, labels, extraRows, testId = "entity-audit-meta",
}) => {
  const { t } = useTranslation();
  const cardTitle = labels?.title ?? title ?? t("common.sections.details", "Szczegóły");
  const lblCreatedAt = labels?.createdAt ?? t("common.audit.created_at", "Utworzono");
  const lblCreatedBy = labels?.createdBy ?? t("common.audit.created_by", "Utworzył(a)");
  const lblUpdatedAt = labels?.updatedAt ?? t("common.audit.updated_at", "Zaktualizowano");
  const lblUpdatedBy = labels?.updatedBy ?? t("common.audit.updated_by", "Modyfikował(a)");
  // Fetch the most recent rows so we have the latest UPDATE actor in scope.
   
  const { query: latest } = useList<ActorRow>({
    resource: "entity_audit_log_view",
    filters: [
      { field: "entity_type", operator: "eq", value: entityType },
      { field: "entity_id",   operator: "eq", value: entityId },
    ],
    sorters: [{ field: "created_at", order: "desc" }],
    pagination: { pageSize: 20, mode: "server" },
    meta: { select: FIRST_LAST_SELECT },
    queryOptions: { enabled: entityId > 0 },
  });

  const rows = latest.data?.data ?? [];
  // First INSERT → creator (rows are sorted desc, so creator is at the end of any UPDATEs).
  const creator = rows.find((r) => r.action === "INSERT") ?? null;
  const lastEditor = rows.find((r) => r.action === "UPDATE") ?? null;

  const items: KVItem[] = [
    ...(extraRows ?? []),
    {
      label: <IconText icon="history" iconSize="sm">{lblCreatedAt}</IconText>,
      value: <DateValue value={createdAt ?? null} variant="timestamp" />,
      sub: creator?.actor_name ?? undefined,
    },
    {
      label: <IconText icon="user" iconSize="sm">{lblCreatedBy}</IconText>,
      value: <TextValue value={creator?.actor_name ?? null} />,
      hidden: !creator?.actor_name,
    },
    {
      label: <IconText icon="history" iconSize="sm">{lblUpdatedAt}</IconText>,
      value: <DateValue value={updatedAt ?? null} variant="timestamp" />,
      sub: lastEditor?.actor_name ?? undefined,
    },
    {
      label: <IconText icon="user" iconSize="sm">{lblUpdatedBy}</IconText>,
      value: <TextValue value={lastEditor?.actor_name ?? null} />,
      hidden: !lastEditor?.actor_name,
    },
  ];

  return (
    <Card title={cardTitle} testId={testId} loading={latest.isLoading && rows.length === 0}>
      <KVGrid items={items} layout="rows" compact />
    </Card>
  );
};
